import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { createClient } from '@workos-inc/authkit-js';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';

type Client = Awaited<ReturnType<typeof createClient>>;

@Injectable({
  providedIn: 'root',
})
export class WorkosService {
  private authkit: Client;
  private initAuthkitPromise: Promise<Client>;

  constructor(private storageService: StorageService) {
    this.initAuthkit();
  }

  private async initAuthkit(mode: 'normal' | 'signIn' = 'normal') {
    const devMode = true;
    // dev mode must be on in order to preserve the connection on the mobile app.
    // It must also be true for the PWA because the legacy app PWA is not on the same domain than
    // our WorkOS custom domain (*.bepark.eu vs *.izix.eu)
    // Explanation on Notion https://www.notion.so/izix-belgium/Technical-notes-1de0bfb233d18085b290f2a2bf7520ec

    // We need to define a different redirectUri when signing in from the native app, as the redirect URI is hosted on 
    // the platform domain and then deep linked to the app. 
    const redirectUri =
      Capacitor.isNativePlatform() && mode === 'signIn'
        ? environment.nativeOAuthRedirectUri
        : environment.pwaOAuthRedirectUri;

    try {
      this.initAuthkitPromise = createClient(environment.workosClientId, { redirectUri, devMode });
      this.authkit = await this.initAuthkitPromise;
    } catch (error) {
      console.error('WorkOS initialization error', error);
    }
  }

  public async getSignInUrl() {
    await this.initAuthkit('signIn');
    return await this.authkit.getSignInUrl();
  }

  public async finishOAuthFlow() {
    await this.initAuthkit();
    const token = await this.getAccessToken();
    this.storageService.setBearer(token);
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      await this.initAuthkitPromise;
      const user = this.authkit.getUser();
      return !!user;
    } catch (error) {
      console.debug('WorkOS isAuthenticated check failed', error);
      return false;
    }
  }

  public getAccessToken() {
    return this.authkit.getAccessToken();
  }

  public switchOrganization(organizationId: string) {
    return this.authkit.switchToOrganization({ organizationId });
  }

  public signOut() {
    if (Capacitor.isNativePlatform()) {
      this.authkit.signOut({ navigate: false });
    } else {
      this.authkit.signOut({ returnTo: environment.pwaOAuthRedirectUri });
    }
  }
}
